import { Button } from "@/shared/ui/button";
import { CheckIcon, LoaderIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface SimpleButtonProps {
    onClick: () => void | Promise<void>;
    children: React.ReactNode;
    isLoading?: boolean;
    isSuccess?: boolean;
    disabled?: boolean;
    className?: string;
    type?: "button" | "submit";
    loadingText?: string;
    successText?: string;
    successDuration?: number; // Duração em ms para mostrar o estado de sucesso
    variant?: "default" | "outline" | "destructive" | "secondary" | "ghost" | "link";
}

export const SimpleButton = ({ 
    onClick, 
    children, 
    isLoading = false,
    isSuccess = false,
    disabled = false,
    className = "",
    type = "button",
    loadingText,
    successText,
    successDuration = 2000, // 2 segundos por padrão
    variant = "default"
}: SimpleButtonProps) => {
    const [showSuccess, setShowSuccess] = useState(false);
    
    // Gerenciar o estado de sucesso com timeout
    useEffect(() => {
        if (isSuccess) {
            setShowSuccess(true);
            const timer = setTimeout(() => {
                setShowSuccess(false);
            }, successDuration);
            
            return () => clearTimeout(timer);
        }
    }, [isSuccess, successDuration]);
    
    const getIcon = () => {
        if (isLoading) return <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />;
        if (showSuccess) return <CheckIcon className="h-4 w-4 mr-2" />;
        return null;
    };
    
    const getButtonText = () => {
        if (isLoading && loadingText) return loadingText;
        if (showSuccess && successText) return successText;
        return children;
    };
    
    const getButtonStyle = () => {
        if (showSuccess) {
            return "bg-green-600 hover:bg-green-700 text-white border-green-600";
        }

        // Estilos baseados na variante
        switch (variant) {
            case "outline":
                return "bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60";
            case "destructive":
                return "bg-red-600 hover:bg-red-700 text-white border-red-600";
            case "secondary":
                return "bg-gray-600 hover:bg-gray-700 text-white border-gray-600";
            case "ghost":
                return "bg-transparent hover:bg-gray-800 text-white border-transparent";
            case "link":
                return "bg-transparent text-blue-400 hover:text-blue-300 underline border-transparent";
            default:
                return "bg-[#D91657] hover:bg-[#D91657]/90 text-white border-[#D91657]";
        }
    };
    
    return (
        <Button 
            type={type}
            onClick={onClick} 
            className={`${getButtonStyle()} ${className}`}
            disabled={disabled || isLoading}
            variant={variant === "default" ? undefined : variant}
        >
            {getIcon()}
            {getButtonText()}
        </Button>
    );
};
